import React, { useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { useDropzone } from "react-dropzone";
import Select from "react-select";
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from "react-bootstrap";

import ConditionalButton from "../../../layout/conditional-button";
import { analyze as analyzeSourceFile, apply as applySourceDataImport } from "../../../actions/source.data.imports";
import { search as searchCourts, get as getCourt } from "../../../actions/courts";
import { IMPORT_FILE_ACCEPT, parseImportFile } from "../../../utils/imports";
import { isGuid } from "../../../utils/string";

const IMPORT_TYPES = [
  { label: "Courts", value: "courts" },
];

const STEPS = [
  "Select Import Type",
  "Upload File",
  "Analyze / Normalize File",
  "Map Court Fields",
  "Generate Import Report",
  "Review Rows",
  "Approve and Import",
  "Completion Summary",
];

const SOURCE_TYPES = [
  { label: "Column", value: "column" },
  { label: "Constant", value: "constant" },
  { label: "NULL", value: "null" },
  { label: "Default", value: "default" },
];

const BOOLEAN_OPTIONS = [
  { label: "Blank", value: null },
  { label: "Yes", value: true },
  { label: "No", value: false },
];

const DUPLICATE_NAME_OPTIONS = [
  { label: "Update", value: "update" },
  { label: "Ignore", value: "ignore" },
  { label: "Report as needing attention", value: "report" },
];

const COURT_FIELDS = [
  { key: "uuid", label: "Court UUID", group: "Court", defaultText: "Generated during import.", matches: ["uuid", "court uuid", "id"] },
  { key: "parentUuid", label: "Parent Court UUID", group: "Court", matches: ["parent uuid", "parent court", "parent"] },
  { key: "name", label: "Name", group: "Court", required: true, matches: ["court name", "name", "court"] },
  { key: "citation", label: "Citation", group: "Court", required: true, matches: ["citation", "cite", "abbreviation"] },
  { key: "full", label: "Description", group: "Court", required: true, matches: ["description", "full", "official name", "court"] },
  { key: "district", label: "District", group: "Court", matches: ["district"] },
  { key: "division", label: "Division", group: "Court", matches: ["division"] },
  { key: "url", label: "URL", group: "Contact", matches: ["url", "website", "web site"] },
  { key: "email", label: "Email", group: "Contact", matches: ["email", "e-mail"] },
  { key: "phone", label: "Phone", group: "Contact", type: "phone", matches: ["main phone", "phone", "telephone"] },
  { key: "chambers", label: "Chambers", group: "Contact", type: "phone", matches: ["chambers", "chamber"] },
  { key: "fax", label: "Fax", group: "Contact", type: "phone", matches: ["fax"] },
  { key: "pacer", label: "PACER", group: "Contact", type: "boolean", matches: ["pacer"] },
  { key: "addressUuid", label: "Address UUID", group: "Address", defaultText: "Generated when address data is imported.", matches: ["address uuid"] },
  { key: "Address.line1", label: "Address Line 1", group: "Address", matches: ["address line 1", "address 1", "line 1", "street", "address"] },
  { key: "Address.line2", label: "Address Line 2", group: "Address", matches: ["address line 2", "address 2", "line 2", "suite"] },
  { key: "Address.city", label: "City", group: "Address", matches: ["city"] },
  { key: "Address.state", label: "State", group: "Address", matches: ["state", "province"] },
  { key: "Address.county", label: "County", group: "Address", matches: ["county"] },
  { key: "Address.zipCode", label: "ZIP Code", group: "Address", type: "zip", matches: ["zip code", "zipcode", "postal", "zip"] },
  { key: "Address.country", label: "Country", group: "Address", defaultValue: "US", matches: ["country"] },
];

const COURT_FIELD_GROUPS = Array.from(new Set(COURT_FIELDS.map((field) => field.group)));
const COURT_FIELD_BY_KEY = Object.fromEntries(COURT_FIELDS.map((field) => [field.key, field]));
const FIELD_LABELS = Object.fromEntries(COURT_FIELDS.map((field) => [field.key, field.label]));
const DUPLICATE_REVIEW_FIELDS = COURT_FIELDS.filter((field) => !["uuid", "addressUuid"].includes(field.key));
const REVIEW_TABLE_STYLE = { minWidth: 1500, tableLayout: "fixed" };
const REVIEW_HEADER_STYLE = { position: "sticky", top: 0, zIndex: 2, background: "#fff" };
const REVIEW_INPUT_STYLE = { minWidth: 0 };

const nextWarnings = (warnings) => Array.from(new Set((warnings || []).filter(Boolean)));

const isXlsxFile = (file) => {
  const name = String(file?.name || "").toLowerCase();
  const type = String(file?.type || "").toLowerCase();
  return name.endsWith(".xlsx") || type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
};

const emptyMapping = (targetField, sourceType = "default") => ({
  targetField,
  sourceType,
  columnKey: null,
  constantValue: COURT_FIELD_BY_KEY[targetField]?.defaultValue ?? "",
});

const labelIncludes = (column, ...terms) => {
  const label = `${column.label || ""} ${column.key || ""}`.toLowerCase();
  return terms.some((term) => label.includes(String(term).toLowerCase()));
};

const pickColumn = (columns, field) => columns.find((column) => labelIncludes(column, ...(field.matches || [])))?.key || null;

const mappingDefaultText = (field) => field.defaultText || (
  field.defaultValue !== undefined ? `Uses ${field.defaultValue}.` : "Left blank."
);

const buildDefaultMappings = (columns = []) => COURT_FIELDS.map((field) => {
  const columnKey = pickColumn(columns, field);
  if (columnKey) return { ...emptyMapping(field.key, "column"), columnKey };
  if (field.defaultValue !== undefined) return { ...emptyMapping(field.key, "constant"), constantValue: field.defaultValue };
  return emptyMapping(field.key);
});

const sourceValueFor = (row, mapping) => {
  if (mapping?.sourceType === "column" && mapping.columnKey) return row.values?.[mapping.columnKey];
  if (mapping?.sourceType === "constant") return isNullLiteral(mapping.constantValue) ? null : mapping.constantValue;
  if (mapping?.sourceType === "null") return null;
  return COURT_FIELD_BY_KEY[mapping?.targetField]?.defaultValue ?? "";
};

const setPath = (object, path, value) => {
  const parts = String(path || "").split(".").filter(Boolean);
  let cursor = object;
  parts.forEach((part, index) => {
    if (index === parts.length - 1) {
      cursor[part] = value;
      return;
    }
    cursor[part] = cursor[part] || {};
    cursor = cursor[part];
  });
};

const getPath = (object, path) => String(path || "")
  .split(".")
  .filter(Boolean)
  .reduce((value, key) => (value == null ? null : value[key]), object);

const hasAddressData = (address = {}) => [
  "line1",
  "line2",
  "city",
  "state",
  "county",
  "zipCode",
].some((field) => String(address?.[field] ?? "").trim() !== "");

const normalizeText = (value) => String(value ?? "").trim();

const isNullLiteral = (value) => String(value ?? "").trim().toUpperCase() === "NULL";

const phoneDigits = (value) => {
  let digits = String(value ?? "").replace(/\D/g, "");
  if (digits.length > 10 && digits.startsWith("1")) digits = digits.slice(1);
  return digits.slice(0, 10);
};

const normalizePhoneForStorage = (value) => {
  const digits = phoneDigits(value);
  return digits.length === 10 ? `+1${digits}` : "";
};

const normalizeZipCode = (value) => {
  const digits = String(value ?? "").replace(/\D/g, "").slice(0, 9);
  if (digits.length === 9) return `${digits.slice(0, 5)}-${digits.slice(5)}`;
  return digits.slice(0, 5);
};

const sanitizeZipCodeInput = (value) => String(value ?? "").replace(/[^\d-]/g, "").slice(0, 10);

const normalizeBoolean = (value) => {
  const text = String(value ?? "").trim().toLowerCase();
  if (!text) return null;
  if (["true", "yes", "y", "1", "enabled", "pacer"].includes(text)) return true;
  if (["false", "no", "n", "0", "disabled"].includes(text)) return false;
  return text;
};

const normalizeFieldValue = (field, value) => {
  if (value === null) return null;
  if (field.type === "phone") return normalizePhoneForStorage(value);
  if (field.type === "zip") return normalizeZipCode(value);
  if (field.type === "boolean") return normalizeBoolean(value);
  if (field.key === "email") return normalizeText(value).toLowerCase();
  if (field.key === "Address.country") return normalizeText(value || "US").toUpperCase();
  return normalizeText(value);
};

const normalizeFieldEditValue = (field, value) => {
  if (value === null) return null;
  if (field.type === "zip") return sanitizeZipCodeInput(value);
  return normalizeFieldValue(field, value);
};

const normalizeAddressCountry = (address) => {
  if (!address) return null;
  return {
    ...address,
    country: address.country === null ? null : normalizeText(address.country || "US").toUpperCase(),
  };
};

const normalizeNameKey = (value) => normalizeText(value).toLowerCase().replace(/\s+/g, " ");

const comparableValue = (value) => {
  if (value === null) return "__NULL__";
  if (value === undefined) return "";
  if (typeof value === "boolean") return value ? "true" : "false";
  return String(value).trim();
};

const formatImportValue = (value) => {
  if (value === null) return "NULL";
  if (value === undefined || value === "") return "(blank)";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};

const duplicateInputId = (rowId, fieldKey, option) => (
  `${rowId}-${fieldKey}-${option}`.replace(/[^A-Za-z0-9_-]/g, "-")
);

const courtValue = (court = {}, field = {}) => {
  if (field.key === "addressUuid") {
    if (court.addressUuid !== undefined) return court.addressUuid;
    if (court.Address?.uuid !== undefined) return court.Address.uuid;
    if (court["Address.uuid"] !== undefined) return court["Address.uuid"];
    return "";
  }
  if (field.key.startsWith("Address.")) {
    const addressField = field.key.slice("Address.".length);
    if (court.Address && Object.prototype.hasOwnProperty.call(court.Address, addressField)) {
      return court.Address[addressField];
    }
    if (court[field.key] !== undefined) return court[field.key];
    return "";
  }
  return court[field.key] !== undefined ? court[field.key] : "";
};

const courtFromExisting = (court = {}) => {
  const normalized = {};
  COURT_FIELDS.forEach((field) => {
    setPath(normalized, field.key, normalizeFieldValue(field, courtValue(court, field)));
  });

  if (!hasAddressData(normalized.Address)) {
    normalized.Address = null;
  } else {
    normalized.Address = normalizeAddressCountry(normalized.Address);
  }

  return normalized;
};

const cloneCourtPayload = (court = {}) => ({
  ...court,
  Address: court.Address ? { ...court.Address } : null,
});

const enforceExistingIdentity = (payload = {}, existing = {}) => {
  const next = cloneCourtPayload(payload);
  next.uuid = existing.uuid || payload.uuid;
  next.addressUuid = existing.addressUuid || existing.Address?.uuid || payload.addressUuid;

  if (next.Address) {
    next.Address = normalizeAddressCountry(next.Address);
  }

  return next;
};

const duplicateConflicts = (current, incoming) => DUPLICATE_REVIEW_FIELDS
  .map((field) => ({
    fieldKey: field.key,
    label: field.label,
    currentValue: getPath(current, field.key),
    proposedValue: getPath(incoming, field.key),
    resolution: null,
  }))
  .filter((item) => comparableValue(item.currentValue) !== comparableValue(item.proposedValue));

const resolvedDuplicatePayload = (incoming, current, conflicts = []) => {
  const proposed = enforceExistingIdentity(incoming, current);
  conflicts.forEach((conflict) => {
    if (conflict.resolution === "current") {
      setPath(proposed, conflict.fieldKey, getPath(current, conflict.fieldKey));
    } else if (conflict.resolution === "proposed") {
      setPath(proposed, conflict.fieldKey, getPath(incoming, conflict.fieldKey));
    }
  });

  if (proposed.Address && !hasAddressData(proposed.Address)) proposed.Address = null;
  if (proposed.Address) proposed.Address = normalizeAddressCountry(proposed.Address);
  return proposed;
};

const duplicateRowStatus = (conflicts, proposed, baseWarnings = []) => {
  const unresolved = (conflicts || []).some((conflict) => !conflict.resolution);
  const errors = validateCourt(proposed);
  if (unresolved) errors.unshift("Choose duplicate values before importing.");
  const warnings = nextWarnings([
    ...baseWarnings,
    "Duplicate name found. Field choices will update the existing court.",
  ]);

  return {
    errors,
    warnings,
    status: errors.length ? "error" : warnings.length ? "warning" : "valid",
    selected: !errors.length,
  };
};

const phoneWarning = (label, rawValue, normalizedValue) => {
  const rawText = String(rawValue ?? "").trim();
  if (!rawText || normalizedValue) return null;
  return `${label} did not contain 10 usable digits and was left blank.`;
};

const validateCourt = (court = {}) => {
  const errors = [];
  if (!normalizeText(court.name)) errors.push("Name is required.");
  if (!normalizeText(court.citation)) errors.push("Citation is required.");
  if (!normalizeText(court.full)) errors.push("Description is required.");

  ["uuid", "parentUuid", "addressUuid"].forEach((field) => {
    const value = normalizeText(court[field]);
    if (value && !isGuid(value)) errors.push(`${FIELD_LABELS[field]} must be a valid UUID.`);
  });

  ["phone", "chambers", "fax"].forEach((field) => {
    const value = normalizeText(court[field]);
    if (value && !/^\+1\d{10}$/.test(value)) errors.push(`${FIELD_LABELS[field]} must use +12345678900 format.`);
  });

  const zipCode = normalizeText(court.Address?.zipCode);
  if (zipCode && !/^\d{5}(-\d{4})?$/.test(zipCode)) errors.push("ZIP Code must use 12345 or 12345-6789 format.");

  if (court.pacer !== null && court.pacer !== undefined && court.pacer !== "" && typeof court.pacer !== "boolean") {
    errors.push("PACER must be Yes, No, or blank.");
  }

  return errors;
};

const normalizeCourtRow = (row, mappings) => {
  const proposed = {};
  const warnings = [];

  mappings.forEach((mapping) => {
    const field = COURT_FIELD_BY_KEY[mapping.targetField];
    if (!field) return;
    const rawValue = sourceValueFor(row, mapping);
    const normalizedValue = normalizeFieldValue(field, rawValue);
    setPath(proposed, field.key, normalizedValue);

    if (field.type === "phone") {
      const warning = phoneWarning(field.label, rawValue, normalizedValue);
      if (warning) warnings.push(warning);
    }
  });

  if (!normalizeText(proposed.full) && normalizeText(proposed.name)) {
    proposed.full = proposed.name;
    warnings.push("Description defaulted to the court name.");
  }

  if (!hasAddressData(proposed.Address)) {
    proposed.Address = null;
  } else {
    proposed.Address = normalizeAddressCountry(proposed.Address);
  }

  const errors = validateCourt(proposed);

  return {
    ...row,
    proposed,
    warnings: nextWarnings(warnings),
    errors,
    status: errors.length ? "error" : warnings.length ? "warning" : "valid",
    selected: !errors.length,
  };
};

const applyDuplicatePolicy = (row, currentCourt, mode) => {
  if (!currentCourt || row.errors?.length) return row;

  const current = courtFromExisting(currentCourt);
  const incoming = cloneCourtPayload(row.proposed);
  const conflicts = duplicateConflicts(current, incoming);
  const currentLabel = current.full || current.name || current.uuid || "existing court";

  if (mode === "ignore") {
    return {
      ...row,
      current,
      proposed: enforceExistingIdentity(incoming, current),
      duplicate: {
        mode,
        current,
        incoming,
        currentLabel,
        conflicts,
        baseWarnings: row.warnings || [],
      },
      warnings: nextWarnings([...(row.warnings || []), "Duplicate name found. This row will be ignored."]),
      errors: [],
      status: "warning",
      selected: false,
      skipImport: true,
    };
  }

  if (mode === "report") {
    const proposed = resolvedDuplicatePayload(incoming, current, conflicts);
    const status = conflicts.length
      ? duplicateRowStatus(conflicts, proposed, row.warnings || [])
      : {
          errors: validateCourt(proposed),
          warnings: nextWarnings([...(row.warnings || []), "Duplicate name found. No field differences were found."]),
          status: "warning",
          selected: true,
        };

    return {
      ...row,
      current,
      proposed,
      duplicate: {
        mode,
        current,
        incoming,
        currentLabel,
        conflicts,
        baseWarnings: row.warnings || [],
      },
      ...status,
    };
  }

  const proposed = enforceExistingIdentity(incoming, current);
  const errors = validateCourt(proposed);
  const warnings = nextWarnings([...(row.warnings || []), "Duplicate name found. Existing court will be updated."]);
  return {
    ...row,
    current,
    proposed,
    duplicate: {
      mode,
      current,
      incoming,
      currentLabel,
      conflicts,
      baseWarnings: row.warnings || [],
    },
    errors,
    warnings,
    status: errors.length ? "error" : "warning",
    selected: !errors.length,
  };
};

const reportSummary = (rows = []) => ({
  total: rows.length,
  valid: rows.filter((row) => row.status === "valid").length,
  warnings: rows.filter((row) => row.status === "warning").length,
  errors: rows.filter((row) => row.status === "error").length,
  selected: rows.filter((row) => row.selected && row.status !== "error" && !row.skipImport).length,
});

function LocalDropzone({ file, onFile }) {
  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    multiple: false,
    accept: IMPORT_FILE_ACCEPT,
    onDrop: (accepted) => onFile(accepted?.[0] || null),
  });

  return (
    <div>
      <div
        {...getRootProps()}
        style={{
          border: "2px dashed #cbd5e1",
          borderRadius: 8,
          padding: 18,
          cursor: "pointer",
          userSelect: "none",
        }}
      >
        <input {...getInputProps()} />
        <div className="fw-bold mb-1">{isDragActive ? "Drop the import file here" : "Upload import file"}</div>
        <div className="small text-muted">
          {file ? `Selected: ${file.name}` : "CSV, JSON, or XLSX only. The file is analyzed temporarily and is not stored."}
        </div>
      </div>
      {fileRejections?.length ? (
        <Alert variant="danger" className="mt-3 mb-0">Only CSV, JSON, and XLSX files are accepted.</Alert>
      ) : null}
    </div>
  );
}

function WizardSteps({ step, disabled, onStep }) {
  return (
    <div className="d-flex flex-wrap border-bottom mb-3">
      {STEPS.map((label, index) => (
        <button
          key={label}
          type="button"
          className={`btn btn-link px-3 py-2 text-decoration-none border-0 border-bottom rounded-0 ${step === index ? "text-primary border-primary fw-semibold" : "text-muted"}`}
          disabled={disabled || index > step}
          onClick={() => onStep(index)}
          style={{
            borderBottomWidth: step === index ? 2 : 0,
            marginBottom: -1,
          }}
        >
          {index + 1}. {label}
        </button>
      ))}
    </div>
  );
}

function MappingRow({ columns, mapping, onChange }) {
  const field = COURT_FIELD_BY_KEY[mapping.targetField];
  const columnOptions = columns.map((column) => ({ value: column.key, label: column.label }));
  const set = (updates) => onChange({ ...mapping, ...updates });

  return (
    <Row className="g-2 align-items-start py-2 border-bottom">
      <Col md="3">
        <div className="fw-bold">
          {field.label}
          {field.required ? <span className="text-danger ms-1">*</span> : null}
        </div>
      </Col>
      <Col md="3">
        <div className="top-label">
          <Select
            value={SOURCE_TYPES.find((item) => item.value === mapping.sourceType)}
            options={SOURCE_TYPES}
            classNamePrefix="react-select"
            onChange={(option) => set({ sourceType: option?.value || "default" })}
          />
          <Form.Label>SOURCE</Form.Label>
        </div>
      </Col>
      <Col md="6">
        {mapping.sourceType === "column" ? (
          <div className="top-label">
            <Select
              value={columnOptions.find((item) => item.value === mapping.columnKey) || null}
              options={columnOptions}
              classNamePrefix="react-select"
              isClearable
              onChange={(option) => set({ columnKey: option?.value || null })}
            />
            <Form.Label>COLUMN</Form.Label>
          </div>
        ) : null}
        {mapping.sourceType === "constant" ? (
          <div className="top-label">
            <Form.Control
              value={mapping.constantValue ?? ""}
              placeholder="NULL"
              onChange={(event) => set({ constantValue: event.target.value })}
            />
            <Form.Label>CONSTANT VALUE</Form.Label>
          </div>
        ) : null}
        {mapping.sourceType === "null" ? (
          <div className="text-muted small pt-2">Imported as NULL.</div>
        ) : null}
        {mapping.sourceType === "default" ? (
          <div className="text-muted small pt-2">{mappingDefaultText(field)}</div>
        ) : null}
      </Col>
    </Row>
  );
}

function ReportStatus({ row }) {
  if (row.status === "error") return <Badge bg="danger">Error</Badge>;
  if (row.status === "warning") return <Badge bg="warning" text="dark">Warning</Badge>;
  return <Badge bg="success">Valid</Badge>;
}

function ReviewField({ label, children }) {
  return (
    <div className="mb-2">
      <div className="small text-muted fw-semibold">{label}</div>
      {children}
    </div>
  );
}

function SourceDataImportWorkflowBase({ show, onHide, analyzeFile: analyzeXlsxFile, applyImport, getCourts, getCourt }) {
  const [step, setStep] = useState(0);
  const [importType, setImportType] = useState(IMPORT_TYPES[0]);
  const [duplicateNameMode, setDuplicateNameMode] = useState(DUPLICATE_NAME_OPTIONS[2]);
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [analyzePreviewLimit, setAnalyzePreviewLimit] = useState(5);
  const [mappings, setMappings] = useState([]);
  const [reportRows, setReportRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const reset = () => {
    setStep(0);
    setImportType(IMPORT_TYPES[0]);
    setDuplicateNameMode(DUPLICATE_NAME_OPTIONS[2]);
    setFile(null);
    setParsed(null);
    setAnalyzePreviewLimit(5);
    setMappings([]);
    setReportRows([]);
    setSummary(null);
    setResults([]);
    setBusy(false);
    setError(null);
  };

  const close = () => {
    reset();
    onHide();
  };

  const analyzeFile = async () => {
    if (!file) {
      setError("Choose a CSV, JSON, or XLSX file first.");
      return false;
    }

    setBusy(true);
    setError(null);
    try {
      const nextParsed = isXlsxFile(file)
        ? await analyzeXlsxFile({ file, dataStart: { row: 1, column: 1 } })
        : await parseImportFile(file);

      if (nextParsed?.success === false) {
        throw new Error(nextParsed?.error?.message || "Unable to analyze the import file.");
      }
      if (!nextParsed?.rows?.length) {
        throw new Error("No import rows were found in the file.");
      }

      setParsed(nextParsed);
      setAnalyzePreviewLimit(5);
      setMappings(buildDefaultMappings(nextParsed.columns));
      setReportRows([]);
      setSummary(null);
      setResults([]);
      return true;
    } catch (err) {
      setError(err?.message || "Unable to analyze the import file.");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const loadDuplicateCourts = async (rows = []) => {
    const result = await getCourts({ size: -1, search: "" });
    const existingItems = Array.isArray(result?.items) ? result.items : Array.isArray(result) ? result : [];
    const byName = new Map();

    existingItems.forEach((item) => {
      const key = normalizeNameKey(item.name);
      if (key && !byName.has(key)) byName.set(key, item);
    });

    const duplicateItems = rows
      .map((row) => byName.get(normalizeNameKey(row.proposed?.name)))
      .filter((item) => item?.uuid);
    const uniqueItems = Array.from(new Map(duplicateItems.map((item) => [item.uuid, item])).values());
    const detailed = await Promise.all(uniqueItems.map(async (item) => {
      try {
        return await getCourt({ uuid: item.uuid });
      } catch {
        return item;
      }
    }));

    return new Map(detailed.filter(Boolean).map((item) => [normalizeNameKey(item.name), item]));
  };

  const generate = async () => {
    setBusy(true);
    setError(null);
    try {
      const normalizedRows = (parsed?.rows || []).map((row) => normalizeCourtRow(row, mappings));
      const duplicateCourts = await loadDuplicateCourts(normalizedRows);
      const rows = normalizedRows.map((row) => applyDuplicatePolicy(
        row,
        duplicateCourts.get(normalizeNameKey(row.proposed?.name)),
        duplicateNameMode?.value || "report"
      ));

      setReportRows(rows);
      setSummary(reportSummary(rows));
      setResults([]);
      return true;
    } catch (err) {
      setError(err?.message || "Unable to generate the import report.");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const reportPayload = () => ({
    importType: importType?.value,
    rows: reportRows,
    selectedRowIds: reportRows
      .filter((row) => row.selected && row.status !== "error" && !row.skipImport)
      .map((row) => row.importRowId),
  });

  const approve = async () => {
    const selectedRowIds = reportPayload().selectedRowIds;
    if (!selectedRowIds.length) {
      setError("Select at least one valid row to import.");
      return false;
    }

    setBusy(true);
    setError(null);
    try {
      const result = await applyImport(reportPayload());
      if (!result?.success) {
        setError(result?.error?.message || "Unable to import the selected rows.");
        return false;
      }
      setSummary(result.summary || null);
      setResults(result.results || []);
      return true;
    } finally {
      setBusy(false);
    }
  };

  const next = async () => {
    if (step === 1) {
      if (!(await analyzeFile())) return;
      setStep(2);
      return;
    }
    if (step === 4) {
      if (!(await generate())) return;
      setStep(5);
      return;
    }
    if (step === 6) {
      if (!(await approve())) return;
      setStep(7);
      return;
    }
    setStep(Math.min(STEPS.length - 1, step + 1));
  };

  const back = () => setStep(Math.max(0, step - 1));

  const goToStep = (targetStep) => {
    if (busy || targetStep > step) return;
    setError(null);
    setStep(targetStep);
  };

  const setMapping = (mapping) => {
    setMappings((prev) => prev.map((item) => item.targetField === mapping.targetField ? mapping : item));
  };

  const toggleRow = (rowId) => {
    setReportRows((prev) => {
      const rows = prev.map((row) => row.importRowId === rowId && !row.skipImport ? { ...row, selected: !row.selected } : row);
      setSummary(reportSummary(rows));
      return rows;
    });
  };

  const updateReportRow = (rowId, fieldKey, value, options = {}) => {
    const field = COURT_FIELD_BY_KEY[fieldKey];
    setReportRows((prev) => {
      const rows = prev.map((row) => {
        if (row.importRowId !== rowId) return row;
        const proposed = {
          ...(row.proposed || {}),
          Address: row.proposed?.Address ? { ...row.proposed.Address } : null,
        };
        setPath(proposed, fieldKey, options.normalize ? normalizeFieldValue(field, value) : normalizeFieldEditValue(field, value));
        if (proposed.Address && !hasAddressData(proposed.Address)) proposed.Address = null;
        if (proposed.Address) proposed.Address = normalizeAddressCountry(proposed.Address);
        const errors = validateCourt(proposed);
        const duplicateNeedsAttention = row.duplicate?.mode === "report"
          && row.duplicate?.conflicts?.some((conflict) => !conflict.resolution);
        if (duplicateNeedsAttention) errors.unshift("Choose duplicate values before importing.");
        const warnings = row.duplicate
          ? nextWarnings([
              ...(row.duplicate.baseWarnings || []),
              row.duplicate.mode === "ignore"
                ? "Duplicate name found. This row will be ignored."
                : "Duplicate name found. Field choices will update the existing court.",
            ])
          : [];
        return {
          ...row,
          proposed,
          errors,
          warnings,
          status: errors.length ? "error" : warnings.length ? "warning" : "valid",
          selected: errors.length || row.skipImport ? false : row.selected,
        };
      });
      setSummary(reportSummary(rows));
      return rows;
    });
  };

  const chooseDuplicateValue = (rowId, fieldKey, resolution) => {
    setReportRows((prev) => {
      const rows = prev.map((row) => {
        if (row.importRowId !== rowId || row.duplicate?.mode !== "report") return row;
        const conflicts = (row.duplicate.conflicts || []).map((conflict) => (
          conflict.fieldKey === fieldKey ? { ...conflict, resolution } : conflict
        ));
        const proposed = resolvedDuplicatePayload(row.duplicate.incoming, row.duplicate.current, conflicts);
        const status = duplicateRowStatus(conflicts, proposed, row.duplicate.baseWarnings || []);
        return {
          ...row,
          proposed,
          duplicate: {
            ...row.duplicate,
            conflicts,
          },
          ...status,
          skipImport: false,
        };
      });
      setSummary(reportSummary(rows));
      return rows;
    });
  };

  const renderSelectType = () => (
    <Row className="g-3">
      <Col md="6">
        <div className="top-label">
          <Select
            value={importType}
            options={IMPORT_TYPES}
            classNamePrefix="react-select"
            onChange={setImportType}
          />
          <Form.Label>IMPORT TYPE</Form.Label>
        </div>
      </Col>
      <Col md="6">
        <div className="top-label">
          <Select
            value={duplicateNameMode}
            options={DUPLICATE_NAME_OPTIONS}
            classNamePrefix="react-select"
            onChange={(option) => {
              setDuplicateNameMode(option || DUPLICATE_NAME_OPTIONS[2]);
              setReportRows([]);
              setSummary(null);
              setResults([]);
              setError(null);
            }}
          />
          <Form.Label>ON DUPLICATE NAME DETECTED</Form.Label>
        </div>
      </Col>
      <Col xs="12">
        <Alert variant="info" className="mb-0">
          Courts imports create or update court source data after mapping, review, and approval.
        </Alert>
      </Col>
    </Row>
  );

  const renderUpload = () => (
    <Row className="g-3">
      <Col xs="12">
        <LocalDropzone file={file} onFile={(nextFile) => {
          setFile(nextFile);
          setParsed(null);
          setAnalyzePreviewLimit(5);
          setMappings([]);
          setReportRows([]);
          setSummary(null);
          setResults([]);
          setError(null);
        }} />
      </Col>
    </Row>
  );

  const renderAnalyze = () => {
    const previewRows = (parsed?.rows || []).slice(0, analyzePreviewLimit);
    const remaining = Math.max(0, (parsed?.rows?.length || 0) - previewRows.length);
    return (
      <Row className="g-3">
        <Col xs="12">
          <Alert variant="info" className="mb-0">
            {parsed?.rows?.length || 0} rows and {parsed?.columns?.length || 0} columns detected.
            {parsed?.hasHeader ? " Headers were detected." : " Indexed column labels were assigned."}
          </Alert>
        </Col>
        <Col xs="12">
          <Table responsive bordered size="sm" className="mb-2">
            <thead>
              <tr>
                <th>Row</th>
                {parsed?.columns?.map((column) => (
                  <th key={column.key}>{column.label}<div className="text-muted small">{column.detectedType}</div></th>
                ))}
              </tr>
            </thead>
            <tbody>
              {previewRows.map((row) => (
                <tr key={row.importRowId}>
                  <td>{row.rowIndex}</td>
                  {parsed.columns.map((column) => <td key={column.key}>{String(row.values[column.key] ?? "")}</td>)}
                </tr>
              ))}
            </tbody>
          </Table>
          {remaining ? (
            <Button
              type="button"
              variant="link"
              className="p-0"
              onClick={() => setAnalyzePreviewLimit((value) => Math.min((parsed?.rows?.length || 0), value + 25))}
            >
              View more ({remaining} remaining)
            </Button>
          ) : null}
        </Col>
      </Row>
    );
  };

  const renderMapping = () => (
    <Row className="g-3">
      <Col xs="12">
        <Alert variant="info" className="mb-0">
          Phone, chambers, fax, and ZIP Code values are normalized before review.
        </Alert>
      </Col>
      {COURT_FIELD_GROUPS.map((group) => (
        <Col xs="12" key={group}>
          <h3 className="small-title">{group}</h3>
          {mappings
            .filter((mapping) => COURT_FIELD_BY_KEY[mapping.targetField]?.group === group)
            .map((mapping) => (
              <MappingRow
                key={mapping.targetField}
                columns={parsed?.columns || []}
                mapping={mapping}
                onChange={setMapping}
              />
            ))}
        </Col>
      ))}
    </Row>
  );

  const renderGenerate = () => (
    <Row className="g-3">
      <Col xs="12">
        <Alert variant="info" className="mb-0">
          Generate the report to normalize court data and check required fields before import.
          Duplicate court names will use: {duplicateNameMode?.label || "Report as needing attention"}.
        </Alert>
      </Col>
      {summary ? (
        <Col xs="12">
          <div className="d-flex gap-2 flex-wrap">
            <Badge bg="secondary">Total {summary.total}</Badge>
            <Badge bg="success">Valid {summary.valid}</Badge>
            <Badge bg="warning" text="dark">Warnings {summary.warnings}</Badge>
            <Badge bg="danger">Errors {summary.errors}</Badge>
          </div>
        </Col>
      ) : null}
    </Row>
  );

  const textControl = (row, fieldKey, label, props = {}) => {
    const field = COURT_FIELD_BY_KEY[fieldKey];
    const isZip = field?.type === "zip";
    return (
      <ReviewField label={label}>
        <Form.Control
          size="sm"
          style={REVIEW_INPUT_STYLE}
          value={getPath(row.proposed, fieldKey) ?? ""}
          aria-label={`${label} row ${row.rowIndex}`}
          inputMode={isZip ? "text" : undefined}
          onChange={(event) => updateReportRow(row.importRowId, fieldKey, event.target.value)}
          onBlur={isZip ? () => updateReportRow(row.importRowId, fieldKey, getPath(row.proposed, fieldKey), { normalize: true }) : undefined}
          {...props}
        />
      </ReviewField>
    );
  };

  const pacerControl = (row) => (
    <ReviewField label="PACER">
      <Select
        value={BOOLEAN_OPTIONS.find((option) => option.value === (row.proposed?.pacer ?? null))}
        options={BOOLEAN_OPTIONS}
        classNamePrefix="react-select"
        onChange={(option) => updateReportRow(row.importRowId, "pacer", option?.value)}
      />
    </ReviewField>
  );

  const renderDuplicateResolution = (row) => {
    if (!row.duplicate) return null;

    if (row.duplicate.mode === "ignore") {
      return (
        <Alert variant="warning" className="mb-0">
          Duplicate name matches {row.duplicate.currentLabel}. This row is marked to be ignored.
        </Alert>
      );
    }

    if (row.duplicate.mode === "update") {
      return (
        <Alert variant="info" className="mb-0">
          Duplicate name matches {row.duplicate.currentLabel}. Importing this row will update the existing court.
        </Alert>
      );
    }

    const conflicts = row.duplicate.conflicts || [];
    return (
      <div className="border rounded p-3 bg-light">
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
          <div>
            <div className="fw-bold">Duplicate name review</div>
            <div className="small text-muted">Matched {row.duplicate.currentLabel}. Choose which value to keep for each field.</div>
          </div>
          <Badge bg={conflicts.some((conflict) => !conflict.resolution) ? "danger" : "success"}>
            {conflicts.some((conflict) => !conflict.resolution) ? "Needs attention" : "Ready"}
          </Badge>
        </div>
        {conflicts.length ? (
          <Row className="g-3">
            {conflicts.map((conflict) => (
              <Col md="6" xl="4" key={conflict.fieldKey}>
                <div className="border rounded p-2 h-100 bg-white">
                  <div className="small text-muted fw-semibold mb-2">{conflict.label}</div>
                  <Form.Check
                    type="radio"
                    id={duplicateInputId(row.importRowId, conflict.fieldKey, "current")}
                    name={`${row.importRowId}-${conflict.fieldKey}`}
                    checked={conflict.resolution === "current"}
                    onChange={() => chooseDuplicateValue(row.importRowId, conflict.fieldKey, "current")}
                    label={<span><span className="fw-semibold">Current:</span> {formatImportValue(conflict.currentValue)}</span>}
                  />
                  <Form.Check
                    type="radio"
                    id={duplicateInputId(row.importRowId, conflict.fieldKey, "proposed")}
                    name={`${row.importRowId}-${conflict.fieldKey}`}
                    checked={conflict.resolution === "proposed"}
                    onChange={() => chooseDuplicateValue(row.importRowId, conflict.fieldKey, "proposed")}
                    label={<span><span className="fw-semibold">Proposed:</span> {formatImportValue(conflict.proposedValue)}</span>}
                  />
                </div>
              </Col>
            ))}
          </Row>
        ) : (
          <div className="text-muted small">No field values differ from the existing court.</div>
        )}
      </div>
    );
  };

  const renderReport = () => (
    <div>
      <div className="d-flex gap-2 flex-wrap mb-3">
        <Badge bg="secondary">Rows {reportRows.length}</Badge>
        <Badge bg="success">Selected {reportRows.filter((row) => row.selected && !row.skipImport).length}</Badge>
        <Badge bg="danger">Errors {reportRows.filter((row) => row.status === "error").length}</Badge>
      </div>
      <div className="table-responsive" style={{ maxHeight: "calc(100vh - 340px)", overflow: "auto" }}>
        <Table bordered hover size="sm" className="mb-0 align-middle" style={REVIEW_TABLE_STYLE}>
          <colgroup>
            <col style={{ width: 74 }} />
            <col style={{ width: "33%" }} />
            <col style={{ width: "25%" }} />
            <col style={{ width: "32%" }} />
            <col style={{ width: "10%" }} />
          </colgroup>
          <thead>
            <tr>
              <th style={REVIEW_HEADER_STYLE}>Row</th>
              <th style={REVIEW_HEADER_STYLE}>Court</th>
              <th style={REVIEW_HEADER_STYLE}>Contact</th>
              <th style={REVIEW_HEADER_STYLE}>Address</th>
              <th style={REVIEW_HEADER_STYLE}>Status</th>
            </tr>
          </thead>
          <tbody>
            {reportRows.map((row) => (
              <React.Fragment key={row.importRowId}>
                <tr className={row.status === "error" ? "table-danger" : row.status === "warning" ? "table-warning" : ""}>
                  <td>
                    <Form.Check
                      checked={Boolean(row.selected)}
                      disabled={row.status === "error" || row.skipImport}
                      aria-label={`Select row ${row.rowIndex}`}
                      onChange={() => toggleRow(row.importRowId)}
                    />
                    <div className="small fw-semibold mt-1">{row.rowIndex}</div>
                  </td>
                  <td>
                    {textControl(row, "name", "Name")}
                    {textControl(row, "citation", "Citation")}
                    {textControl(row, "full", "Description", { as: "textarea", rows: 2 })}
                    <Row className="g-2">
                      <Col sm="6">{textControl(row, "district", "District")}</Col>
                      <Col sm="6">{textControl(row, "division", "Division")}</Col>
                    </Row>
                    {textControl(row, "uuid", "Court UUID", row.duplicate ? { readOnly: true } : {})}
                    {textControl(row, "parentUuid", "Parent Court UUID")}
                  </td>
                  <td>
                    {textControl(row, "url", "URL")}
                    {textControl(row, "email", "Email")}
                    {textControl(row, "phone", "Phone")}
                    {textControl(row, "chambers", "Chambers")}
                    {textControl(row, "fax", "Fax")}
                    {pacerControl(row)}
                  </td>
                  <td>
                    {textControl(row, "Address.line1", "Line 1")}
                    {textControl(row, "Address.line2", "Line 2")}
                    <Row className="g-2">
                      <Col sm="6">{textControl(row, "Address.city", "City")}</Col>
                      <Col sm="6">{textControl(row, "Address.state", "State")}</Col>
                    </Row>
                    <Row className="g-2">
                      <Col sm="6">{textControl(row, "Address.county", "County")}</Col>
                      <Col sm="6">{textControl(row, "Address.zipCode", "ZIP Code")}</Col>
                    </Row>
                    {textControl(row, "Address.country", "Country")}
                    {textControl(row, "addressUuid", "Address UUID", row.duplicate ? { readOnly: true } : {})}
                  </td>
                  <td>
                    <ReportStatus row={row} />
                    {(row.errors || []).map((message) => <div key={message} className="small text-danger mt-1">{message}</div>)}
                    {(row.warnings || []).map((message) => <div key={message} className="small text-muted mt-1">{message}</div>)}
                  </td>
                </tr>
                {row.duplicate ? (
                  <tr>
                    <td />
                    <td colSpan="4">{renderDuplicateResolution(row)}</td>
                  </tr>
                ) : null}
              </React.Fragment>
            ))}
          </tbody>
        </Table>
      </div>
    </div>
  );

  const renderApprove = () => {
    const selected = reportRows.filter((row) => row.selected && row.status !== "error" && !row.skipImport);
    const blocked = reportRows.filter((row) => row.selected && row.status === "error");
    const ignored = reportRows.filter((row) => row.skipImport);
    return (
      <Row className="g-3">
        <Col xs="12">
          <Alert variant={blocked.length ? "warning" : "info"} className="mb-0">
            {selected.length} valid row{selected.length === 1 ? "" : "s"} will be imported.
            {blocked.length ? ` ${blocked.length} selected row(s) still have blocking errors and will be skipped.` : ""}
            {ignored.length ? ` ${ignored.length} duplicate row(s) are marked ignore.` : ""}
          </Alert>
        </Col>
      </Row>
    );
  };

  const renderComplete = () => (
    <Row className="g-3">
      <Col xs="12">
        <Alert variant={Number(summary?.failed || 0) ? "warning" : "success"} className="mb-0">
          Source data import completed.
        </Alert>
      </Col>
      <Col md="3"><div className="border rounded p-3"><div className="text-muted small">Total Rows</div><div className="h4 mb-0">{summary?.total ?? 0}</div></div></Col>
      <Col md="3"><div className="border rounded p-3"><div className="text-muted small">Selected</div><div className="h4 mb-0">{summary?.selected ?? 0}</div></div></Col>
      <Col md="3"><div className="border rounded p-3"><div className="text-muted small">Imported</div><div className="h4 mb-0 text-success">{summary?.imported ?? 0}</div></div></Col>
      <Col md="3"><div className="border rounded p-3"><div className="text-muted small">Skipped / Failed</div><div className="h4 mb-0 text-warning">{Number(summary?.skipped || 0) + Number(summary?.failed || 0)}</div></div></Col>
      {results.filter((result) => !result.success && !result.skipped).length ? (
        <Col xs="12">
          <Alert variant="danger" className="mb-0">
            {results.filter((result) => !result.success && !result.skipped).map((result) => (
              <div key={result.importRowId}>
                Row {result.rowIndex}: {result.error?.message || "Unable to import row."}
              </div>
            ))}
          </Alert>
        </Col>
      ) : null}
    </Row>
  );

  const body = [
    renderSelectType,
    renderUpload,
    renderAnalyze,
    renderMapping,
    renderGenerate,
    renderReport,
    renderApprove,
    renderComplete,
  ][step];

  if (!show) return null;

  return (
    <Col xs="12">
      <Card>
        <Card.Body>
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <div>
              <h2 className="small-title mb-1">Import Source Data</h2>
              <div className="text-muted small">Temporary import review for firm source data.</div>
            </div>
            <Badge bg="secondary">{STEPS[step]}</Badge>
          </div>

          <WizardSteps step={step} disabled={busy} onStep={goToStep} />

          {error ? <Alert variant="danger">{error}</Alert> : null}
          {busy ? (
            <div className="text-center py-5"><Spinner animation="border" /></div>
          ) : body()}

          <div className="d-flex justify-content-between gap-2 mt-4 pt-3 border-top">
            <div>
              <ConditionalButton enabledVariant="outline-primary" display enabled={!busy} onEnabledClick={close} icon="error-hexagon">
                cancel
              </ConditionalButton>
            </div>
            <div className="d-flex gap-2 flex-wrap justify-content-end">
              {step > 0 && step < 7 ? (
                <ConditionalButton enabledVariant="outline-primary" display enabled={!busy} onEnabledClick={back} icon="arrow-left">
                  back
                </ConditionalButton>
              ) : null}
              {step < 7 ? (
                <ConditionalButton enabledVariant="outline-primary" display enabled={!busy} onEnabledClick={next} icon={step === 6 ? "upload" : "arrow-right"}>
                  {step === 4 ? "Generate" : step === 6 ? "Import" : "Next"}
                </ConditionalButton>
              ) : (
                <ConditionalButton enabledVariant="outline-primary" display enabled onEnabledClick={close} icon="check">
                  Done
                </ConditionalButton>
              )}
            </div>
          </div>
        </Card.Body>
      </Card>
    </Col>
  );
}

SourceDataImportWorkflowBase.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  analyzeFile: PropTypes.func.isRequired,
  applyImport: PropTypes.func.isRequired,
  getCourts: PropTypes.func.isRequired,
  getCourt: PropTypes.func.isRequired,
};

export const SourceDataImportWorkflow = connect(null, {
  analyzeFile: analyzeSourceFile,
  applyImport: applySourceDataImport,
  getCourts: searchCourts,
  getCourt,
})(SourceDataImportWorkflowBase);

export default function SourceDataImportButton({ onStart, disabled }) {
  return (
    <ConditionalButton
      enabledVariant="outline-primary"
      tooltip="Import source data"
      display
      enabled={!disabled}
      onEnabledClick={onStart}
      icon="upload"
    >
      Import Source Data
    </ConditionalButton>
  );
}

SourceDataImportButton.propTypes = {
  onStart: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

SourceDataImportButton.defaultProps = {
  disabled: false,
};
